import React, { useEffect, useMemo, useState } from 'react';
import { X, Copy, Check, Sparkles } from 'lucide-react';
import { File } from '../types';
import { Button } from './ui/button';

interface AISidebarProps {
  open: boolean;
  width: number;
  onWidthChange: (px: number) => void;
  onClose: () => void;
  file: File;
  content: string;
  onContentUpdate: (content: string) => void;
}

const MIN_W = 280;
const MAX_W = 700;

const AISidebar: React.FC<AISidebarProps> = ({
  open,
  width,
  onWidthChange,
  onClose,
  file,
  content,
  onContentUpdate,
}) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState<'explain' | 'generate' | ''>('');
  const [result, setResult] = useState('');
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [resizing, setResizing] = useState(false);

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e: MouseEvent) => {
      const next = Math.min(
        Math.max(window.innerWidth - e.clientX, MIN_W),
        MAX_W
      );
      onWidthChange(next);
    };
    const onUp = () => setResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing, onWidthChange]);

  const startStream = async (kind: 'explain' | 'generate') => {
    setLoading(true);
    setAction(kind);
    setResult('');
    try {
      const res = await fetch('/api/ai/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({
          action: kind,
          code: content,
          language: file.language,
          prompt,
        }),
      });
      if (!res.ok || !res.body) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || 'Failed to stream');
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events separated by double newline
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const eventBlock = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = eventBlock.split('\n');
          for (const l of lines) {
            if (!l.startsWith('data:')) continue;
            const dataStr = l.replace(/^data:\s?/, '');
            if (!dataStr || dataStr === '[DONE]') continue;
            try {
              const json = JSON.parse(dataStr);
              const choice = Array.isArray(json.choices)
                ? json.choices[0]
                : undefined;
              const delta =
                choice?.delta?.content ?? choice?.message?.content ?? '';
              if (typeof delta === 'string' && delta) {
                setResult(prev => prev + delta);
              }
              // Some providers stream as {content: "..."}
              else if (typeof json.content === 'string') {
                setResult(prev => prev + json.content);
              }
            } catch {
              // ignore non-JSON or partial lines to avoid dumping JSON
            }
          }
        }
      }
    } catch (err) {
      setResult('Failed to stream AI response');
    } finally {
      setLoading(false);
    }
  };

  const renderInline = (text: string): React.ReactNode[] => {
    const nodes: React.ReactNode[] = [];
    const parts = text.split(/(`[^`]+`)/g);
    parts.forEach((part, idx) => {
      if (part.startsWith('`') && part.endsWith('`')) {
        nodes.push(
          <code
            key={`code-${idx}`}
            className='rounded bg-muted px-1 py-0.5 text-xs'
          >
            {part.slice(1, -1)}
          </code>
        );
      } else {
        // Links
        const linkRegex = /\[([^\]]+)\]\((https?:[^)]+)\)/g;
        let lastIndex = 0;
        const linkPieces: React.ReactNode[] = [];
        let m: RegExpExecArray | null;
        while ((m = linkRegex.exec(part)) !== null) {
          if (m.index > lastIndex)
            linkPieces.push(part.slice(lastIndex, m.index));
          linkPieces.push(
            <a
              key={`a-${idx}-${m.index}`}
              href={m[2]}
              target='_blank'
              rel='noreferrer'
              className='underline'
            >
              {m[1]}
            </a>
          );
          lastIndex = linkRegex.lastIndex;
        }
        if (lastIndex < part.length) linkPieces.push(part.slice(lastIndex));

        // Bold then italics
        const applyStyles = (
          node: React.ReactNode,
          keyPrefix: string
        ): React.ReactNode[] => {
          if (typeof node !== 'string') return [node];
          const boldRegex = /\*\*(.+?)\*\*/g;
          const italicRegex = /\*(.+?)\*/g;

          const boldOut: React.ReactNode[] = [];
          let last = 0;
          let mb: RegExpExecArray | null;
          while ((mb = boldRegex.exec(node)) !== null) {
            if (mb.index > last) boldOut.push(node.slice(last, mb.index));
            boldOut.push(
              <strong key={`${keyPrefix}-b-${mb.index}`}>{mb[1]}</strong>
            );
            last = boldRegex.lastIndex;
          }
          if (last < node.length) boldOut.push(node.slice(last));

          const italicsOut: React.ReactNode[] = [];
          boldOut.forEach((frag, j) => {
            if (typeof frag !== 'string') {
              italicsOut.push(frag);
              return;
            }
            let lastI = 0;
            let mi: RegExpExecArray | null;
            while ((mi = italicRegex.exec(frag)) !== null) {
              if (mi.index > lastI)
                italicsOut.push(frag.slice(lastI, mi.index));
              italicsOut.push(
                <em key={`${keyPrefix}-i-${j}-${mi.index}`}>{mi[1]}</em>
              );
              lastI = italicRegex.lastIndex;
            }
            if (lastI < frag.length) italicsOut.push(frag.slice(lastI));
          });
          return italicsOut;
        };

        linkPieces.forEach((p, j) => {
          applyStyles(p, `${idx}-${j}`).forEach(n => nodes.push(n));
        });
      }
    });
    return nodes;
  };

  const MarkdownText: React.FC<{ text: string }> = ({ text }) => {
    const elements: React.ReactNode[] = [];
    const lines = text.split('\n');
    let i = 0;

    const flushList = (items: string[], ordered: boolean) => {
      if (items.length === 0) return;
      elements.push(
        ordered ? (
          <ol
            className='list-decimal pl-5 space-y-1'
            key={`ol-${elements.length}`}
          >
            {items.map((t, idx) => (
              <li key={idx}>{renderInline(t)}</li>
            ))}
          </ol>
        ) : (
          <ul
            className='list-disc pl-5 space-y-1'
            key={`ul-${elements.length}`}
          >
            {items.map((t, idx) => (
              <li key={idx}>{renderInline(t)}</li>
            ))}
          </ul>
        )
      );
    };

    while (i < lines.length) {
      const line = lines[i];
      // Headings
      const h = /^(#{1,6})\s+(.+)$/.exec(line);
      if (h) {
        const level = h[1].length;
        const content = h[2];
        const Tag = `h${Math.min(6, level)}` as keyof JSX.IntrinsicElements;
        elements.push(
          <Tag key={`h-${i}`} className='font-semibold mt-2'>
            {renderInline(content)}
          </Tag>
        );
        i++;
        continue;
      }

      // Unordered list
      if (/^[-*]\s+/.test(line)) {
        const items: string[] = [];
        while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
          items.push(lines[i].replace(/^[-*]\s+/, ''));
          i++;
        }
        flushList(items, false);
        continue;
      }

      // Ordered list
      if (/^\d+\.\s+/.test(line)) {
        const items: string[] = [];
        while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
          items.push(lines[i].replace(/^\d+\.\s+/, ''));
          i++;
        }
        flushList(items, true);
        continue;
      }

      // Horizontal rule
      if (/^---+$/.test(line.trim())) {
        elements.push(<hr key={`hr-${i}`} className='my-2 border-muted' />);
        i++;
        continue;
      }

      // Paragraph (can be empty for spacing)
      elements.push(
        <p key={`p-${i}`} className='text-sm whitespace-pre-wrap'>
          {renderInline(line)}
        </p>
      );
      i++;
    }

    return <>{elements}</>;
  };

  // Removed apply-to-editor per design request

  const segments = useMemo(() => {
    // very small markdown/code splitter for triple-backticks
    const arr: Array<{
      type: 'text' | 'code';
      language?: string;
      value: string;
    }> = [];
    const regex = /```(\w+)?\n([\s\S]*?)```/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(result)) !== null) {
      if (match.index > lastIndex) {
        arr.push({ type: 'text', value: result.slice(lastIndex, match.index) });
      }
      arr.push({
        type: 'code',
        language: (match[1] || 'text') as string,
        value: match[2],
      });
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < result.length)
      arr.push({ type: 'text', value: result.slice(lastIndex) });
    return arr;
  }, [result]);

  const copy = async (text: string, idx?: number) => {
    await navigator.clipboard.writeText(text);
    if (typeof idx === 'number') {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    }
  };

  if (!open) return null;

  return (
    <div
      className='h-full border-l bg-background relative flex flex-col'
      style={{ width }}
    >
      {/* Header */}
      <div className='sticky top-0 z-10 flex items-center justify-between border-b bg-background px-3 py-2'>
        <div className='flex items-center gap-2'>
          <Sparkles size={16} />
          <h3 className='m-0 text-sm font-medium'>Sayu</h3>
        </div>
        <button
          className='text-muted-foreground hover:text-foreground'
          onClick={onClose}
          aria-label='Close Sayu'
        >
          <X size={16} />
        </button>
      </div>

      {/* Content */}
      <div className='flex-1 overflow-y-auto p-3 space-y-3 pb-20'>
        <div className='space-y-2'>
          <label className='text-xs text-muted-foreground'>Prompt</label>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                startStream('generate');
              }
            }}
            placeholder='Write me a function that… (Shift+Enter for newline)'
            rows={3}
            className='w-full rounded-md border-2 border-emerald-400 bg-background px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-emerald-500'
          />
        </div>
        {segments.length === 0 && !loading && (
          <div className='text-xs text-muted-foreground'>
            Ask Sayu to explain the current file or generate code from your
            prompt.
          </div>
        )}
        {segments.map((seg, idx) =>
          seg.type === 'text' ? (
            <div key={idx} className='text-sm'>
              <MarkdownText text={seg.value} />
            </div>
          ) : (
            <div key={idx} className='rounded-md bg-muted p-2'>
              <div className='mb-1 flex items-center justify-between text-[10px] uppercase tracking-wide text-muted-foreground'>
                <span>{seg.language}</span>
                <button
                  className='inline-flex items-center gap-1 text-foreground/80 hover:text-foreground'
                  onClick={() => copy(seg.value, idx)}
                >
                  {copiedIdx === idx ? <Check size={12} /> : <Copy size={12} />}
                  Copy
                </button>
              </div>
              <pre
                className='whitespace-pre-wrap text-xs overflow-auto'
                style={{ maxHeight: 320 }}
              >
                {seg.value}
              </pre>
            </div>
          )
        )}
        {loading && (
          <div className='text-center text-xs text-muted-foreground'>
            🤖 Streaming…
          </div>
        )}
        {action === 'generate' && result && (
          <div className='mt-2 flex justify-end gap-2'>
            <Button
              size='sm'
              variant='secondary'
              onClick={() => copy(result)}
              className='inline-flex items-center gap-1'
            >
              <Copy size={12} /> Copy all
            </Button>
          </div>
        )}
      </div>

      {/* Bottom-right actions */}
      <div className='absolute bottom-3 right-3 flex gap-2'>
        <Button
          size='sm'
          variant='secondary'
          onClick={() => startStream('explain')}
          disabled={loading}
          className='h-12 px-4'
        >
          Explain
        </Button>
        <Button
          size='sm'
          onClick={() => startStream('generate')}
          disabled={loading}
          className='h-12 px-4'
        >
          Enter
        </Button>
      </div>

      {/* Resizer */}
      <div
        className='absolute top-0 left-[-3px] h-full w-1 cursor-col-resize bg-border hover:bg-muted'
        onMouseDown={() => setResizing(true)}
        aria-label='Resize Sayu panel'
      />
    </div>
  );
};

export default AISidebar;
